import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { ScatterplotLayer, IconLayer, TextLayer } from '@deck.gl/layers';
import { getAvatar, ensureAvatar } from '../utils/avatarCache';
import './Map.css';

const MAP_ID = 'ssn-gems-map';
const LABEL_MIN_ZOOM = 7;
const DEFAULT_CLUSTER_MAX_ZOOM = 14;
const DATE_LABEL_FIELDS = new Set(['dueDate', 'scheduleDate', 'startDate']);

/** Format ticket marker label text; dates as M/D/YYYY. Empty dates stay blank. */
function formatTicketLabelText(d, labelField) {
  if (DATE_LABEL_FIELDS.has(labelField)) {
    const raw = d[labelField];
    if (!raw) return '';
    const s = String(raw);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [y, m, day] = s.slice(0, 10).split('-');
      return `${parseInt(m, 10)}/${parseInt(day, 10)}/${y}`;
    }
    return s;
  }
  return String(d[labelField] ?? d.siteName ?? d.recordId ?? '');
}

const roundBadgeCache = new Map();

/** Circular count badge (stack totals and open-message counts). */
function roundBadgeIcon(label, fill) {
  const key = `${fill}|${label}`;
  const cached = roundBadgeCache.get(key);
  if (cached) return cached;
  const text = String(label);
  const size = text.length >= 3 ? 32 : text.length === 2 ? 26 : 22;
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = size * scale;
  canvas.height = size * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1.2, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${text.length >= 3 ? 10 : text.length === 2 ? 11 : 12}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, size / 2, size / 2 + 0.5);
  const icon = {
    url: canvas.toDataURL(),
    width: size * scale,
    height: size * scale,
    mask: false,
  };
  roundBadgeCache.set(key, icon);
  return icon;
}

function badgeSize(label) {
  const text = String(label);
  return text.length >= 3 ? 32 : text.length === 2 ? 26 : 22;
}

/**
 * Project a lat/lng into pixels inside the map div so the chat can sit on the icon.
 */
function ChatAnchorTracker({ anchor, onPoint }) {
  const map = useMap();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map || !anchor) {
      onPoint(null);
      return undefined;
    }
    if (!overlayRef.current) {
      const overlay = new window.google.maps.OverlayView();
      overlay.onAdd = () => {};
      overlay.draw = () => {};
      overlay.onRemove = () => {};
      overlay.setMap(map);
      overlayRef.current = overlay;
    }
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const projection = overlayRef.current?.getProjection();
        if (!projection) return;
        const latLng = new window.google.maps.LatLng(anchor.lat, anchor.lng);
        const pt = projection.fromLatLngToContainerPixel(latLng);
        if (!pt) return;
        const div = map.getDiv();
        onPoint({
          x: pt.x,
          y: pt.y,
          width: div.clientWidth,
          height: div.clientHeight,
        });
      });
    };
    update();
    const listeners = ['idle', 'bounds_changed'].map(name => map.addListener(name, update));
    return () => {
      cancelAnimationFrame(raf);
      listeners.forEach(listener => window.google.maps.event.removeListener(listener));
    };
  }, [map, anchor, onPoint]);

  useEffect(() => () => {
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }
  }, []);

  return null;
}

// Soft disc behind a checked-in site. Stays green until the check-in is
// older than 12 hours (yellow) or 24 hours (red), measured in site time.
const CHECKIN_GREEN = [22, 163, 74, 90];
const CHECKIN_YELLOW = [234, 179, 8, 120];
const CHECKIN_RED = [220, 38, 38, 120];
const CHECKIN_YELLOW_AFTER_HOURS = 12;
const CHECKIN_RED_AFTER_HOURS = 24;

const SITE_TIME_ZONES = {
  ET: 'America/New_York', EST: 'America/New_York', EDT: 'America/New_York',
  CT: 'America/Chicago', CST: 'America/Chicago', CDT: 'America/Chicago',
  MT: 'America/Denver', MST: 'America/Denver', MDT: 'America/Denver',
  PT: 'America/Los_Angeles', PST: 'America/Los_Angeles', PDT: 'America/Los_Angeles',
  AKT: 'America/Anchorage', AKST: 'America/Anchorage', AKDT: 'America/Anchorage',
  HT: 'Pacific/Honolulu', HST: 'Pacific/Honolulu', HAST: 'Pacific/Honolulu',
  AT: 'America/Puerto_Rico', AST: 'America/Puerto_Rico',
};
const UNASSIGNED_FILL = [255, 237, 213];   // Light amber fill
const UNASSIGNED_BORDER = [245, 158, 11];  // Amber #f59e0b
const ASSIGNED_FILL = [219, 234, 254];     // Light blue fill
const ASSIGNED_BORDER = [59, 130, 246];    // Blue #3b82f6

// Rating tier colors for field reps
const RATING_COLORS = {
  5: [76, 175, 80],    // #4CAF50 green
  4: [139, 195, 74],   // #8BC34A light green
  3: [255, 193, 7],    // #FFC107 yellow
  2: [255, 152, 0],    // #FF9800 orange
  1: [244, 67, 54],    // #F44336 red
  0: [158, 158, 158],  // #9E9E9E grey
};

function getRatingTier(rating) {
  const r = parseFloat(rating) || 0;
  if (r >= 4.5) return 5;
  if (r >= 3.5) return 4;
  if (r >= 2.5) return 3;
  if (r >= 1.5) return 2;
  if (r >= 0.5) return 1;
  return 0;
}

// Recency color scale for live-rep rings. Fixed buckets (independent of the
// window slider) so a color always means the same age.
function recencyColor(ageMinutes) {
  const a = ageMinutes ?? Infinity;
  if (a <= 15) return [76, 175, 80];    // green — just pinged
  if (a <= 60) return [139, 195, 74];   // light green — recent
  if (a <= 240) return [255, 193, 7];   // amber — getting stale
  if (a <= 720) return [255, 152, 0];   // orange — old
  return [158, 158, 158];               // grey — stale
}

// Relative age for marker labels: "12m" / "2h" / "1d".
function formatAge(minutes) {
  const m = Math.max(0, Math.round(minutes ?? 0));
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
}

function hexToRgb(hex) {
  if (!hex) return [128, 128, 128];
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [128, 128, 128];
}

// Helper: point-in-polygon for lasso selection
function pointInPolygon(point, polygon) {
  const [lng, lat] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect = ((yi > lat) !== (yj > lat))
      && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function markerIsSelected(d, selectedIds) {
  if (!selectedIds || selectedIds.size === 0) return false;
  if (selectedIds.has(d.id)) return true;
  if (d._isStack && d._stackMembers) {
    return d._stackMembers.some(m => m.id && selectedIds.has(m.id));
  }
  return false;
}

function nativeEventFromPickingInfo(info) {
  const ev = info?.srcEvent || info?.event;
  if (!ev) return null;
  // Google Maps MouseEvent puts modifiers on domEvent, not the Maps wrapper.
  return ev.domEvent || ev.originalEvent || ev.srcEvent || ev;
}

function clickEventIsAdditive(info, shiftHeld = false) {
  if (shiftHeld) return true;
  const native = nativeEventFromPickingInfo(info);
  return !!(native && native.shiftKey);
}

// Deck.gl's Google Maps overlay wraps the browser event: picking info →
// deck event → google.maps.MapMouseEvent → DOM MouseEvent. clientX lives
// only on the DOM event, so stop at the first object that has it.
function unwrapDomEvent(event) {
  let ev = event || null;
  for (let i = 0; ev && i < 5; i++) {
    if (typeof ev.clientX === 'number' && typeof ev.clientY === 'number') return ev;
    const next = ev.domEvent || ev.originalEvent || ev.srcEvent;
    if (!next || next === ev) break;
    ev = next;
  }
  return null;
}

// Deck.gl rewrites Google's rightclick into a plain click, and that DOM event
// often reports button 0. Remember the real gesture long enough for every
// listener (including ones queued on a timer) to see it.
let rightClickUntil = 0;
let menuOpenedThisGesture = false;

function beginRightClickGesture() {
  rightClickUntil = Date.now() + 400;
  menuOpenedThisGesture = false;
}

function markRightClickGesture() {
  rightClickUntil = Date.now() + 400;
}

function noteMenuOpened() {
  menuOpenedThisGesture = true;
}

function rightClickActive() {
  return Date.now() < rightClickUntil;
}

function eventIsRightClick(event) {
  let ev = event || null;
  for (let i = 0; ev && i < 5; i++) {
    if (ev.button === 2 || ev.which === 3 || ev.type === 'contextmenu' || ev.type === 'rightclick') {
      return true;
    }
    const next = ev.domEvent || ev.originalEvent || ev.srcEvent;
    if (!next || next === ev) break;
    ev = next;
  }
  return false;
}

function markerClickMeta(info, deckEvent) {
  const raw = deckEvent || info?.srcEvent || info?.event;
  const dom = unwrapDomEvent(raw);
  let point = null;
  if (dom) {
    point = { x: dom.clientX, y: dom.clientY };
  } else if (info && Number.isFinite(info.x) && Number.isFinite(info.y)) {
    const rect = document.querySelector('.coord-map-area')?.getBoundingClientRect();
    if (rect) point = { x: rect.left + info.x, y: rect.top + info.y };
  }
  const rightClick = rightClickActive() || eventIsRightClick(raw);
  return { shiftKey: clickEventIsAdditive(info), point, rightClick };
}

function menuMarker(object) {
  if (!object) return null;
  // Grid clusters have a count plus `members`. Stacked pins also have a
  // count, but they are a real pin (`_isStack`) and should open the menu.
  if (object.count > 1 && Array.isArray(object.members) && !object._isStack) return null;
  if (object.recordId || object.repId || object._layerType || object._isStack || object._live) return object;
  return null;
}

/**
 * Grid-based clustering: groups nearby points into clusters at low zoom
 * Returns { singles: [], clusters: [] }
 */
function clusterPoints(items, getLng, getLat, zoom, maxZoom) {
  if (zoom >= maxZoom) return { singles: items, clusters: [] };

  // Grid cell size in degrees — smaller divisor = tighter clusters
  const cellSize = 360 / Math.pow(2, zoom + 3);
  const grid = new Map();

  for (const item of items) {
    const lng = getLng(item);
    const lat = getLat(item);
    const cellX = Math.floor(lng / cellSize);
    const cellY = Math.floor(lat / cellSize);
    const key = `${cellX}:${cellY}`;
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(item);
  }

  const singles = [];
  const clusters = [];

  for (const members of grid.values()) {
    if (members.length === 1) {
      singles.push(members[0]);
    } else {
      const avgLng = members.reduce((s, m) => s + getLng(m), 0) / members.length;
      const avgLat = members.reduce((s, m) => s + getLat(m), 0) / members.length;
      clusters.push({
        id: `cluster-${avgLat.toFixed(4)}-${avgLng.toFixed(4)}`,
        longitude: avgLng,
        latitude: avgLat,
        count: members.length,
        members,
      });
    }
  }

  return { singles, clusters };
}

/**
 * Detect co-located markers (same or nearly same coordinates).
 * Returns { unique: [], stacks: [] } where stacks are groups of 2+ at same spot.
 */
function detectStacks(items, getLng, getLat, idPrefix = 'stack', sortFn = null) {
  const precision = 10000; // ~11m resolution
  const grid = new Map();

  for (const item of items) {
    const key = `${Math.round(getLat(item) * precision)}:${Math.round(getLng(item) * precision)}`;
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(item);
  }

  const unique = [];
  const stacks = [];

  for (const members of grid.values()) {
    if (members.length === 1) {
      unique.push(members[0]);
    } else {
      // Sort so the highest-priority member (e.g. best-rated rep) is first
      const ordered = sortFn ? [...members].sort(sortFn) : members;
      const lng = getLng(ordered[0]);
      const lat = getLat(ordered[0]);
      stacks.push({
        id: `${idPrefix}-${lat.toFixed(4)}-${lng.toFixed(4)}`,
        longitude: lng,
        latitude: lat,
        count: ordered.length,
        members: ordered,
        // Also expose as a single marker so it renders with proper icon/color
        ...ordered[0],
        _isStack: true,
        _stackCount: ordered.length,
        _stackMembers: ordered,
      });
    }
  }

  return { unique, stacks };
}

/**
 * Component that handles right-click (desktop) and empty-map click to deselect.
 * Marker picks go through deck.gl; we suppress the immediate map click that
 * can follow a marker selection so the selection is not cleared instantly.
 */
function DeselectHandler({ onDeselect, suppressRef, messageHoverRef, lockClickDeselect = false, shiftHeldRef, gestureShiftRef }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const mapDiv = map.getDiv();
    const inMap = (target) => target instanceof Node && mapDiv.contains(target);
    // Window capture runs before Google's map listener, so the gesture is
    // already marked when deck.gl turns the right-click into a plain click.
    const onMouseDown = (e) => {
      if (e.button === 2 && inMap(e.target)) beginRightClickGesture();
    };
    const onContextMenu = (e) => {
      if (!inMap(e.target)) return;
      markRightClickGesture();
      e.preventDefault();
    };
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('contextmenu', onContextMenu, true);
    const onClick = map.addListener('click', () => {
      if (suppressRef?.current) return;
      if (messageHoverRef?.current) return;
      if (lockClickDeselect) return;
      // Same Google Maps `click` also drives deck.gl picking. This listener
      // is registered first, so without this guard a Shift-click clears the
      // current ticket before the marker handler can add the new one.
      if (shiftHeldRef?.current || gestureShiftRef?.current) return;
      onDeselect();
    });
    return () => {
      window.google.maps.event.removeListener(onClick);
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, [map, onDeselect, suppressRef, messageHoverRef, lockClickDeselect, shiftHeldRef, gestureShiftRef]);

  return null;
}

/** Records whether the current pointer gesture started with Shift. */
function PointerGestureTracker({ shiftHeldRef, gestureShiftRef, suppressDeselectRef }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const readShift = (e) => !!(e?.domEvent && e.domEvent.shiftKey);
    const onDown = (e) => {
      const shift = readShift(e) || !!shiftHeldRef.current;
      if (shift) shiftHeldRef.current = true;
      gestureShiftRef.current = shift;
      if (shift) {
        map.setOptions({ draggable: false });
        // Block the map `click` deselect that fires before deck.gl picking.
        if (suppressDeselectRef) suppressDeselectRef.current = true;
      }
    };
    const onUp = (e) => {
      if (readShift(e)) gestureShiftRef.current = true;
      map.setOptions({ draggable: true });
    };
    const downL = map.addListener('mousedown', onDown);
    const upL = map.addListener('mouseup', onUp);
    return () => {
      window.google.maps.event.removeListener(downL);
      window.google.maps.event.removeListener(upL);
    };
  }, [map, shiftHeldRef, gestureShiftRef, suppressDeselectRef]);
  return null;
}

/**
 * Shift+drag lasso over ticket markers. Field reps are ignored.
 * Completing a lasso with 0 tickets leaves the current selection alone.
 */
function LassoSelectHandler({ tickets, onLassoSelect, suppressRef, shiftHeldRef, enabled = true }) {
  const map = useMap();
  const [drawingPath, setDrawingPath] = useState([]);
  const polygonRef = useRef(null);
  const isDrawingRef = useRef(false);
  const drawingPathRef = useRef([]);
  const pendingRef = useRef(null);
  const localShiftRef = useRef(false);

  useEffect(() => {
    drawingPathRef.current = drawingPath;
  }, [drawingPath]);

  const shiftIsDown = () => !!(shiftHeldRef?.current || localShiftRef.current);

  useEffect(() => {
    if (!map || !enabled) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        localShiftRef.current = true;
        if (shiftHeldRef) shiftHeldRef.current = true;
        map.setOptions({ draggable: false });
      }
    };
    const handleKeyUp = (e) => {
      if (e.key !== 'Shift') return;
      localShiftRef.current = false;
      if (shiftHeldRef) shiftHeldRef.current = false;
      map.setOptions({ draggable: true });
      pendingRef.current = null;
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        setDrawingPath([]);
        if (polygonRef.current) {
          polygonRef.current.setMap(null);
          polygonRef.current = null;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      map.setOptions({ draggable: true });
    };
  }, [map, enabled, shiftHeldRef]);

  useEffect(() => {
    if (!map || !enabled) return;

    // Don't treat a Shift-click as a lasso. Wait until the pointer has
    // actually dragged so a click can add to the selection instead.
    const DRAG_THRESHOLD_PX = 24;

    const handleMouseDown = (e) => {
      const native = e.domEvent;
      if (native?.shiftKey) {
        localShiftRef.current = true;
        if (shiftHeldRef) shiftHeldRef.current = true;
      }
      if (!e.latLng || !shiftIsDown()) return;
      pendingRef.current = {
        x: native?.clientX ?? 0,
        y: native?.clientY ?? 0,
        lng: e.latLng.lng(),
        lat: e.latLng.lat(),
      };
    };

    const handleMouseMove = (e) => {
      if (!e.latLng) return;
      if (pendingRef.current && !isDrawingRef.current) {
        const native = e.domEvent;
        const dx = (native?.clientX ?? 0) - pendingRef.current.x;
        const dy = (native?.clientY ?? 0) - pendingRef.current.y;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        isDrawingRef.current = true;
        setDrawingPath([
          [pendingRef.current.lng, pendingRef.current.lat],
          [e.latLng.lng(), e.latLng.lat()],
        ]);
        return;
      }
      if (!isDrawingRef.current) return;
      setDrawingPath(prev => [...prev, [e.latLng.lng(), e.latLng.lat()]]);
    };

    const handleMouseUp = () => {
      pendingRef.current = null;
      if (!isDrawingRef.current) return;
      const currentPath = drawingPathRef.current;
      isDrawingRef.current = false;

      if (currentPath.length >= 3 && onLassoSelect) {
        const selected = (tickets || []).filter(t => {
          const lng = t.siteLongitude;
          const lat = t.siteLatitude;
          if (!isFinite(lng) || !isFinite(lat)) return false;
          return pointInPolygon([lng, lat], currentPath);
        });
        if (suppressRef) suppressRef.current = true;
        onLassoSelect(selected);
        window.setTimeout(() => {
          if (suppressRef) suppressRef.current = false;
        }, 80);
      }

      setDrawingPath([]);
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };

    const mouseDownListener = map.addListener('mousedown', handleMouseDown);
    const mouseMoveListener = map.addListener('mousemove', handleMouseMove);
    const mouseUpListener = map.addListener('mouseup', handleMouseUp);
    return () => {
      window.google.maps.event.removeListener(mouseDownListener);
      window.google.maps.event.removeListener(mouseMoveListener);
      window.google.maps.event.removeListener(mouseUpListener);
    };
  }, [map, tickets, onLassoSelect, suppressRef, enabled]);

  useEffect(() => {
    if (!map || !enabled || !isDrawingRef.current || drawingPath.length === 0) return;
    const path = drawingPath.map(([lng, lat]) => ({ lat, lng }));
    if (!polygonRef.current) {
      polygonRef.current = new window.google.maps.Polygon({
        strokeColor: '#2563eb',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
        map,
        clickable: false,
      });
    }
    polygonRef.current.setPath(path);
  }, [map, drawingPath, enabled]);

  useEffect(() => {
    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, []);

  return null;
}

function getMarkerLatLng(marker) {
  if (!marker) return null;
  if (marker._live && isFinite(marker.lat) && isFinite(marker.lng)) {
    return { lat: marker.lat, lng: marker.lng };
  }
  if (marker._layerType === 'fieldRep' && isFinite(marker.repLat) && isFinite(marker.repLong)) {
    return { lat: marker.repLat, lng: marker.repLong };
  }
  if (isFinite(marker.siteLatitude) && isFinite(marker.siteLongitude)) {
    return { lat: marker.siteLatitude, lng: marker.siteLongitude };
  }
  if (isFinite(marker.latitude) && isFinite(marker.longitude)) {
    return { lat: marker.latitude, lng: marker.longitude };
  }
  if (isFinite(marker.repLat) && isFinite(marker.repLong)) {
    return { lat: marker.repLat, lng: marker.repLong };
  }
  if (isFinite(marker.lat) && isFinite(marker.lng)) {
    return { lat: marker.lat, lng: marker.lng };
  }
  return null;
}

/**
 * When a mobile detail sheet is open, pan so the selected marker sits in the
 * visible band above the sheet (~top 30% of the map).
 */
function FocusSelectedForSheet({ selectedMarker, enabled, sheetHeightRatio = 0.7 }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !enabled || !selectedMarker) return;

    const pos = getMarkerLatLng(selectedMarker);
    if (!pos) return;

    const t = window.setTimeout(() => {
      map.panTo(pos);
      const h = map.getDiv()?.offsetHeight || 0;
      if (h <= 0) return;
      const visibleBand = h * (1 - sheetHeightRatio);
      const targetY = Math.max(48, visibleBand * 0.55);
      const centerY = h / 2;
      map.panBy(0, centerY - targetY);
    }, 80);

    return () => clearTimeout(t);
  }, [map, enabled, selectedMarker, sheetHeightRatio]);

  return null;
}

function openMessageCount(marker) {
  const members = marker._isStack && marker._stackMembers ? marker._stackMembers : [marker];
  return members.reduce((n, m) => n + (Number(m._openMessages) || 0), 0);
}

/** Tickets in a pin, stack, or cluster that currently have check-in data. */
function checkInMembers(marker) {
  if (!marker || marker._layerType === 'fieldRep' || marker._live) return [];
  let members;
  if (marker._isStack && Array.isArray(marker._stackMembers)) {
    members = marker._stackMembers;
  } else if (
    Array.isArray(marker.members)
    && marker.members.some(m => m && (m.recordId || m.checkIn))
  ) {
    members = marker.members;
  } else {
    members = [marker];
  }
  return members.filter(m => m && m.checkIn);
}

function siteIanaZone(checkIn) {
  const key = String(checkIn?.timeZone || '').trim().toUpperCase();
  if (!key) return null;
  if ((key === 'MST' || key === 'MT') && checkIn.observesDst === false) return 'America/Phoenix';
  return SITE_TIME_ZONES[key] || null;
}

/** Minutes the zone is ahead of UTC at this instant. */
function tzOffsetMinutes(timeZone, utcMs) {
  const parts = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs))) {
    if (part.type !== 'literal') parts[part.type] = part.value;
  }
  let hour = Number(parts.hour);
  let day = Number(parts.day);
  if (hour === 24) {
    hour = 0;
    day += 1;
  }
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    day,
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  return (asUtc - utcMs) / 60000;
}

/** "M/D/YYYY h:mm AM site time" in the site's zone, as a UTC timestamp. */
function siteTimeToUtcMs(label, timeZone) {
  const match = String(label || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match || !timeZone) return null;
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const ampm = match[6].toUpperCase();
  if (ampm === 'PM' && hour < 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  const wallAsUtc = Date.UTC(Number(match[3]), Number(match[1]) - 1, Number(match[2]), hour, minute);
  const offset = tzOffsetMinutes(timeZone, wallAsUtc);
  let utc = wallAsUtc - offset * 60000;
  const offset2 = tzOffsetMinutes(timeZone, utc);
  if (offset2 !== offset) utc = wallAsUtc - offset2 * 60000;
  return utc;
}

function checkInAgeHours(checkIn, now) {
  const utc = siteTimeToUtcMs(checkIn?.checkedInLabel, siteIanaZone(checkIn));
  if (utc == null || !Number.isFinite(utc)) return null;
  return (now - utc) / 3600000;
}

/** Oldest check-in on a pin decides the glow. Unknown times stay green. */
function checkInGlowColor(marker, now) {
  let oldest = 0;
  let known = false;
  for (const member of checkInMembers(marker)) {
    const age = checkInAgeHours(member.checkIn, now);
    if (age == null) continue;
    known = true;
    if (age > oldest) oldest = age;
  }
  if (known && oldest > CHECKIN_RED_AFTER_HOURS) return CHECKIN_RED;
  if (known && oldest > CHECKIN_YELLOW_AFTER_HOURS) return CHECKIN_YELLOW;
  return CHECKIN_GREEN;
}

function checkInHoverLines(marker) {
  const hits = checkInMembers(marker);
  const qualify = hits.length > 1
    || !!(marker && (marker._isStack || (Array.isArray(marker.members) && marker.members.length > 1)));
  return hits.map(m => {
    const who = m.checkIn.repName || 'Checked in';
    const body = m.checkIn.distanceLabel ? `${who} · ${m.checkIn.distanceLabel}` : who;
    return qualify && m.siteName ? `${m.siteName} — ${body}` : body;
  });
}

const checkMarkCache = { icon: null };

/** Small green circle with a white check, drawn once and reused. */
function checkMarkIcon() {
  if (checkMarkCache.icon) return checkMarkCache.icon;
  const size = 18;
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = size * scale;
  canvas.height = size * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1.2, 0, Math.PI * 2);
  ctx.fillStyle = '#16a34a';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(size * 0.28, size * 0.52);
  ctx.lineTo(size * 0.44, size * 0.70);
  ctx.lineTo(size * 0.74, size * 0.34);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
  checkMarkCache.icon = {
    url: canvas.toDataURL(),
    width: size * scale,
    height: size * scale,
    mask: false,
  };
  return checkMarkCache.icon;
}

function checkInBadgeIcon(count) {
  return count > 1 ? roundBadgeIcon(count, '#16a34a') : checkMarkIcon();
}

function CoordinatorOverlay({ tickets, fieldReps, liveReps, selectedIds, onMarkerClick, onMessageClick, onEmptyRightClick, messageHoverRef, iconConfig, clusterMaxZoom, ticketLabelField, labelBackground = true, labelVisibility = { tickets: true, fieldReps: true, liveReps: true } }) {
  const selectedKey = selectedIds && selectedIds.size ? [...selectedIds].join('|') : '';
  const map = useMap();
  const overlayRef = useRef(null);
  const [shouldShowLabels, setShouldShowLabels] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(5);
  // Bumped when an avatar finishes compositing so the layers rebuild with it.
  const [avatarTick, setAvatarTick] = useState(0);
  const [checkInHover, setCheckInHover] = useState(null);
  const [checkInClock, setCheckInClock] = useState(() => Date.now());

  // Recolor long check-ins while the map stays open.
  useEffect(() => {
    const id = setInterval(() => setCheckInClock(Date.now()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Pointer cursor on markers. Checked-in pins also get a name/distance tip.
  const handleHover = useCallback(info => {
    if (!map) return;
    if (info.object) {
      map.setOptions({ draggableCursor: 'pointer' });
      const lines = checkInHoverLines(info.object);
      setCheckInHover(lines.length ? { x: info.x, y: info.y, lines } : null);
    } else {
      map.setOptions({ draggableCursor: null });
      setCheckInHover(null);
    }
  }, [map]);

  // Track zoom for labels and clustering
  useEffect(() => {
    if (!map) return;
    const check = () => {
      const zoom = map.getZoom();
      setCurrentZoom(zoom);
      setShouldShowLabels(prev => {
        const visible = zoom >= LABEL_MIN_ZOOM;
        return prev !== visible ? visible : prev;
      });
    };
    check();
    const listener = map.addListener('zoom_changed', check);
    return () => window.google.maps.event.removeListener(listener);
  }, [map]);

  // Build and update layers
  useEffect(() => {
    if (!map) return;

    const timeout = setTimeout(() => {
      const layers = [];

      // Soft off-white chip behind label text for readability over busy map
      // areas. Spread into each label TextLayer; empty when toggled off.
      const labelBg = labelBackground ? {
        background: true,
        getBackgroundColor: [255, 255, 255, 200],
        backgroundPadding: [6, 3, 6, 3],
      } : {};

      // Cluster tickets
      const effectiveClusterZoom = clusterMaxZoom || DEFAULT_CLUSTER_MAX_ZOOM;
      const { singles: rawSingleTickets, clusters: ticketClusters } = clusterPoints(
        tickets, d => d.siteLongitude, d => d.siteLatitude, currentZoom, effectiveClusterZoom
      );

      // Detect co-located markers among singles (overlapping at same spot)
      const { unique: singleTickets, stacks: ticketStacks } = detectStacks(
        rawSingleTickets, d => d.siteLongitude, d => d.siteLatitude
      );

      // Ticket cluster circles
      if (ticketClusters.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'ticket-clusters',
          data: ticketClusters,
          pickable: true,
          opacity: 0.9,
          stroked: true,
          filled: true,
          radiusMinPixels: 16,
          radiusMaxPixels: 50,
          lineWidthMinPixels: 2,
          getPosition: d => [d.longitude, d.latitude],
          getRadius: d => Math.min(40, 16 + Math.log2(d.count) * 5),
          getFillColor: [59, 130, 246, 200],
          getLineColor: [255, 255, 255],
          getLineWidth: 2,
          onHover: handleHover,
          onClick: (info, event) => {
            if (markerClickMeta(info, event).rightClick) return;
            if (info.object && map) {
              map.setZoom(Math.min(currentZoom + 3, effectiveClusterZoom));
              map.panTo({ lat: info.object.latitude, lng: info.object.longitude });
            }
          },
        }));

        // Cluster count labels
        layers.push(new TextLayer({
          id: 'ticket-cluster-labels',
          data: ticketClusters,
          pickable: false,
          getPosition: d => [d.longitude, d.latitude],
          getText: d => String(d.count),
          getSize: 13,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'bold',
          getColor: [255, 255, 255, 255],
        }));

        const checkedClusters = ticketClusters.filter(c => checkInMembers(c).length > 0);
        if (checkedClusters.length > 0) {
          layers.push(new IconLayer({
            id: 'ticket-cluster-checkins',
            data: checkedClusters,
            pickable: true,
            getPosition: d => [d.longitude, d.latitude],
            getIcon: d => checkInBadgeIcon(checkInMembers(d).length),
            getSize: d => {
              const n = checkInMembers(d).length;
              return n > 1 ? badgeSize(n) : 18;
            },
            getPixelOffset: [0, 20],
            onHover: handleHover,
            onClick: (info, event) => {
              if (markerClickMeta(info, event).rightClick) return;
              if (info.object && map) {
                map.setZoom(Math.min(currentZoom + 3, effectiveClusterZoom));
                map.panTo({ lat: info.object.latitude, lng: info.object.longitude });
              }
            },
          }));
        }
      }

      // Combine singles and stacks for rendering (stacks render like their first marker)
      const allSingleTickets = [...singleTickets, ...ticketStacks];

      // Soft green disc behind any site (or stack) that has someone checked in.
      const checkedInTickets = allSingleTickets.filter(t => checkInMembers(t).length > 0);
      if (checkedInTickets.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'ticket-checkin-halos',
          data: checkedInTickets,
          pickable: true,
          opacity: 1,
          stroked: false,
          filled: true,
          radiusUnits: 'pixels',
          radiusMinPixels: 34,
          radiusMaxPixels: 34,
          getPosition: d => [d.siteLongitude ?? d.longitude, d.siteLatitude ?? d.latitude],
          getRadius: 34,
          getFillColor: d => checkInGlowColor(d, checkInClock),
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
        }));
      }

      // Single ticket dots (unclustered or zoom is high enough)
      const ticketsWithoutIcons = allSingleTickets.filter(t => !t._iconUrl);
      if (ticketsWithoutIcons.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'tickets-dots',
          data: ticketsWithoutIcons,
          pickable: true,
          opacity: 1,
          stroked: true,
          filled: true,
          radiusMinPixels: 10,
          radiusMaxPixels: 10,
          lineWidthMinPixels: 3,
          getPosition: d => [d.siteLongitude, d.siteLatitude],
          getRadius: 10,
          getFillColor: d => d._layerType === 'unassigned' ? UNASSIGNED_FILL : ASSIGNED_FILL,
          getLineColor: d => {
            if (markerIsSelected(d, selectedIds)) return [0, 0, 0];
            return d._layerType === 'unassigned' ? UNASSIGNED_BORDER : ASSIGNED_BORDER;
          },
          getLineWidth: d => markerIsSelected(d, selectedIds) ? 15 : 3,
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
          updateTriggers: {
            getLineColor: [selectedKey],
            getLineWidth: [selectedKey],
          }
        }));
      }

      // Single ticket icons (unclustered)
      const ticketsWithIcons = allSingleTickets.filter(t => t._iconUrl);

      // Border rings behind icons (orange for unassigned, blue for assigned)
      // This layer is pickable — acts as the click target for icon markers
      if (ticketsWithIcons.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'tickets-icon-borders',
          data: ticketsWithIcons,
          pickable: true,
          opacity: 1,
          stroked: true,
          filled: false,
          radiusMinPixels: 21,
          radiusMaxPixels: 21,
          lineWidthMinPixels: 6,
          lineWidthMaxPixels: 6,
          getPosition: d => [d.siteLongitude, d.siteLatitude],
          getRadius: 21,
          getFillColor: [0, 0, 0, 0],
          getLineColor: d => {
            if (markerIsSelected(d, selectedIds)) return [0, 0, 0];
            return d._layerType === 'unassigned' ? UNASSIGNED_BORDER : ASSIGNED_BORDER;
          },
          getLineWidth: d => markerIsSelected(d, selectedIds) ? 15 : 6,
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
          updateTriggers: {
            getLineColor: [selectedKey],
            getLineWidth: [selectedKey],
          }
        }));
      }

      // Icon images on top of border rings (visual only, not pickable)
      if (ticketsWithIcons.length > 0) {
        layers.push(new IconLayer({
          id: 'tickets-icons',
          data: ticketsWithIcons,
          pickable: true,
          getPosition: d => [d.siteLongitude, d.siteLatitude],
          getIcon: d => ({
            url: d._iconUrl,
            width: 40,
            height: 40,
            anchorY: 20,
          }),
          getSize: d => markerIsSelected(d, selectedIds) ? 48 : 36,
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
          onIconError: ({ url }) => console.warn('Failed to load icon:', url),
          loadOptions: {
            fetch: {
              mode: 'cors',
              cache: 'reload',  // Bypass browser cache for CORS-fixed icons
            }
          },
          updateTriggers: {
            getSize: [selectedKey],
          }
        }));
      }

      // Ticket labels (only when not clustering)
      if (shouldShowLabels && labelVisibility.tickets && allSingleTickets.length > 0) {
        const labelField = ticketLabelField || 'siteName';

        // Regular labels for non-selected markers
        const unselectedTickets = allSingleTickets.filter(d => !markerIsSelected(d, selectedIds));
        if (unselectedTickets.length > 0) {
          layers.push(new TextLayer({
            id: 'ticket-labels',
            ...labelBg,
            data: unselectedTickets,
            pickable: false,
            getPosition: d => [d.siteLongitude, d.siteLatitude],
            getText: d => formatTicketLabelText(d, labelField),
            getSize: 14,
            getTextAnchor: 'start',
            getAlignmentBaseline: 'center',
            getPixelOffset: [24, 0],
            fontFamily: 'Arial, sans-serif',
            getColor: [0, 0, 0, 255],
            outlineWidth: 2,
            outlineColor: [255, 255, 255, 255],
            updateTriggers: {
              getText: [labelField],
            }
          }));
        }

        // Enhanced label for selected marker
        const selectedTicket = allSingleTickets.filter(d => markerIsSelected(d, selectedIds));
        if (selectedTicket.length > 0) {
          layers.push(new TextLayer({
            id: 'ticket-label-selected',
            ...labelBg,
            data: selectedTicket,
            pickable: false,
            getPosition: d => [d.siteLongitude, d.siteLatitude],
            getText: d => formatTicketLabelText(d, labelField),
            getSize: 17,
            fontWeight: 'bold',
            getTextAnchor: 'start',
            getAlignmentBaseline: 'center',
            getPixelOffset: [24, 0],
            fontFamily: 'Arial, sans-serif',
            getColor: [0, 0, 0, 255],
            outlineWidth: 3,
            outlineColor: [255, 255, 255, 255],
            updateTriggers: {
              getText: [labelField],
            }
          }));
        }
      }

      // Check (or a count, when several stacked tickets are checked in) at the
      // bottom of the green disc. Top corners belong to messages and stack counts.
      if (checkedInTickets.length > 0) {
        layers.push(new IconLayer({
          id: 'ticket-checkin-marks',
          data: checkedInTickets,
          pickable: true,
          getPosition: d => [d.siteLongitude ?? d.longitude, d.siteLatitude ?? d.latitude],
          getIcon: d => checkInBadgeIcon(checkInMembers(d).length),
          getSize: d => {
            const n = checkInMembers(d).length;
            return n > 1 ? badgeSize(n) : 18;
          },
          getPixelOffset: [0, 26],
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
        }));
      }

      // Stack count badges — round, top-right of overlapping ticket icons
      if (ticketStacks.length > 0) {
        layers.push(new IconLayer({
          id: 'ticket-stack-badges',
          data: ticketStacks,
          pickable: false,
          getPosition: d => [d.longitude, d.latitude],
          getIcon: d => roundBadgeIcon(d._stackCount, '#dc2626'),
          getSize: d => badgeSize(d._stackCount),
          getPixelOffset: [16, -16],
        }));
      }

      // Cluster field reps
      const { singles: rawSingleReps, clusters: repClusters } = clusterPoints(
        fieldReps, d => d.repLong, d => d.repLat, currentZoom, effectiveClusterZoom
      );

      // Detect co-located reps (multiple reps at same coordinates).
      // Sort by rating desc so the highest-rated rep is the visible one.
      const { unique: uniqueReps, stacks: repStacks } = detectStacks(
        rawSingleReps, d => d.repLong, d => d.repLat, 'rep-stack',
        (a, b) => (b.rating || 0) - (a.rating || 0)
      );
      const allSingleReps = [...uniqueReps, ...repStacks];

      // Rep cluster circles
      if (repClusters.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'rep-clusters',
          data: repClusters,
          pickable: true,
          opacity: 0.85,
          stroked: true,
          filled: true,
          radiusMinPixels: 14,
          radiusMaxPixels: 40,
          lineWidthMinPixels: 1,
          getPosition: d => [d.longitude, d.latitude],
          getRadius: d => Math.min(30, 14 + Math.log2(d.count) * 4),
          getFillColor: [76, 175, 80, 180],
          getLineColor: [255, 255, 255],
          getLineWidth: 1,
          onHover: handleHover,
          onClick: (info, event) => {
            if (markerClickMeta(info, event).rightClick) return;
            if (info.object && map) {
              map.setZoom(Math.min(currentZoom + 3, effectiveClusterZoom));
              map.panTo({ lat: info.object.latitude, lng: info.object.longitude });
            }
          },
        }));

        layers.push(new TextLayer({
          id: 'rep-cluster-labels',
          data: repClusters,
          pickable: false,
          getPosition: d => [d.longitude, d.latitude],
          getText: d => String(d.count),
          getSize: 11,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'bold',
          getColor: [255, 255, 255, 255],
        }));
      }

      // Single field rep dots
      if (allSingleReps.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'field-reps',
          data: allSingleReps,
          pickable: true,
          opacity: 0.85,
          stroked: true,
          filled: true,
          radiusMinPixels: 8,
          radiusMaxPixels: 8,
          lineWidthMinPixels: 2,
          getPosition: d => [d.repLong, d.repLat],
          getRadius: 8,
          getFillColor: d => {
            const tier = getRatingTier(d.rating);
            return RATING_COLORS[tier] || RATING_COLORS[0];
          },
          getLineColor: d => markerIsSelected(d, selectedIds) ? [0, 0, 0] : [255, 255, 255],
          getLineWidth: d => markerIsSelected(d, selectedIds) ? 10 : 2,
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
          updateTriggers: {
            getLineColor: [selectedKey],
            getLineWidth: [selectedKey],
          }
        }));
      }

      // Field Rep labels
      if (shouldShowLabels && labelVisibility.fieldReps && allSingleReps.length > 0) {
        const unselectedReps = allSingleReps.filter(d => !markerIsSelected(d, selectedIds));
        if (unselectedReps.length > 0) {
          layers.push(new TextLayer({
            id: 'rep-labels',
            ...labelBg,
            data: unselectedReps,
            pickable: false,
            getPosition: d => [d.repLong, d.repLat],
            getText: d => d.name || '',
            getSize: 14,
            getTextAnchor: 'start',
            getAlignmentBaseline: 'center',
            getPixelOffset: [12, 0],
            fontFamily: 'Arial, sans-serif',
            getColor: [50, 50, 50, 255],
            outlineWidth: 2,
            outlineColor: [255, 255, 255, 255],
          }));
        }

        const selectedRep = allSingleReps.filter(d => markerIsSelected(d, selectedIds));
        if (selectedRep.length > 0) {
          layers.push(new TextLayer({
            id: 'rep-label-selected',
            ...labelBg,
            data: selectedRep,
            pickable: false,
            getPosition: d => [d.repLong, d.repLat],
            getText: d => d.name || '',
            getSize: 17,
            fontWeight: 'bold',
            getTextAnchor: 'start',
            getAlignmentBaseline: 'center',
            getPixelOffset: [12, 0],
            fontFamily: 'Arial, sans-serif',
            getColor: [0, 0, 0, 255],
            outlineWidth: 3,
            outlineColor: [255, 255, 255, 255],
          }));
        }
      }

      // Stack count badges for co-located reps
      if (repStacks.length > 0) {
        layers.push(new IconLayer({
          id: 'rep-stack-badges',
          data: repStacks,
          pickable: false,
          getPosition: d => [d.repLong, d.repLat],
          getIcon: d => roundBadgeIcon(d._stackCount, '#dc2626'),
          getSize: d => badgeSize(d._stackCount),
          getPixelOffset: [14, -14],
        }));
      }

      // ===== Live rep locations (photo avatar + recency ring) =====
      if (liveReps && liveReps.length > 0) {
        // Kick off avatar compositing for any rep we haven't drawn yet.
        for (const lr of liveReps) {
          if (!getAvatar(lr.repId)) {
            ensureAvatar(lr.repId, lr.name, () => setAvatarTick(t => t + 1));
          }
        }

        // Recency ring — also the click target. Drawn for every live rep so a
        // marker appears immediately, before its photo finishes compositing.
        layers.push(new ScatterplotLayer({
          id: 'live-rep-rings',
          data: liveReps,
          pickable: true,
          stroked: true,
          filled: true,
          radiusMinPixels: 20,
          radiusMaxPixels: 20,
          lineWidthMinPixels: 5,
          lineWidthMaxPixels: 5,
          getPosition: d => [d.lng, d.lat],
          getRadius: 20,
          getFillColor: [255, 255, 255, 235],
          getLineColor: d => markerIsSelected(d, selectedIds) ? [0, 0, 0] : recencyColor(d.ageMinutes),
          getLineWidth: d => markerIsSelected(d, selectedIds) ? 12 : 5,
          onHover: handleHover,
          onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
          updateTriggers: {
            getLineColor: [selectedKey, avatarTick],
            getLineWidth: [selectedKey],
          },
        }));

        // Circular avatars — only for reps whose avatar has composited.
        const avatarReady = liveReps.filter(d => getAvatar(d.repId));
        if (avatarReady.length > 0) {
          layers.push(new IconLayer({
            id: 'live-rep-avatars',
            data: avatarReady,
            pickable: true,
            getPosition: d => [d.lng, d.lat],
            getIcon: d => ({ url: getAvatar(d.repId), width: 72, height: 72 }),
            getSize: d => markerIsSelected(d, selectedIds) ? 44 : 34,
            onHover: handleHover,
            onClick: (info, event) => info.object && onMarkerClick(info.object, markerClickMeta(info, event)),
            updateTriggers: {
              getIcon: [avatarTick],
              getSize: [selectedKey],
            },
          }));
        }

        // Labels: "Name · age"
        if (shouldShowLabels && labelVisibility.liveReps) {
          layers.push(new TextLayer({
            id: 'live-rep-labels',
            ...labelBg,
            data: liveReps,
            pickable: false,
            getPosition: d => [d.lng, d.lat],
            getText: d => `${d.name || 'Rep'} · ${formatAge(d.ageMinutes)}`,
            getSize: 13,
            getTextAnchor: 'start',
            getAlignmentBaseline: 'center',
            getPixelOffset: [22, 0],
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            getColor: [30, 30, 30, 255],
            outlineWidth: 2,
            outlineColor: [255, 255, 255, 255],
            updateTriggers: {
              getText: [avatarTick],
            },
          }));
        }
      }

      // Open-message counts. Top-left of the icon so they don't sit on the
      // red stack badge (top-right). Click opens the chat, not the ticket panel.
      const messageBadges = allSingleTickets
        .map(d => {
          const count = openMessageCount(d);
          return count > 0 ? { ...d, _openMessageCount: count } : null;
        })
        .filter(Boolean);
      if (messageBadges.length > 0) {
        const messageBadgeKey = messageBadges.map(d => `${d.recordId}:${d._openMessageCount}`).join('|');
        layers.push(new IconLayer({
          id: 'ticket-message-badges',
          data: messageBadges,
          pickable: true,
          getPosition: d => [d.siteLongitude, d.siteLatitude],
          getIcon: d => roundBadgeIcon(d._openMessageCount > 99 ? '99+' : d._openMessageCount, '#2563eb'),
          getSize: d => badgeSize(d._openMessageCount > 99 ? '99+' : d._openMessageCount),
          getPixelOffset: [-16, -16],
          onHover: info => {
            handleHover(info);
            if (messageHoverRef) messageHoverRef.current = !!info.object;
          },
          onClick: (info, event) => {
            if (!info.object) return;
            const meta = markerClickMeta(info, event);
            if (meta.rightClick) {
              onMarkerClick(info.object, meta);
              return;
            }
            if (onMessageClick) onMessageClick(info.object);
          },
          updateTriggers: {
            getIcon: [messageBadgeKey],
            getSize: [messageBadgeKey],
          },
        }));
      }

      // Create or update overlay
      if (!overlayRef.current) {
        overlayRef.current = new GoogleMapsOverlay({
          layers,
          pickingRadius: 10,
        });
        overlayRef.current.setMap(map);
      } else {
        overlayRef.current.setProps({ layers });
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [map, tickets, fieldReps, liveReps, selectedIds, selectedKey, onMarkerClick, onMessageClick, messageHoverRef, iconConfig, shouldShowLabels, currentZoom, avatarTick, labelBackground, ticketLabelField, clusterMaxZoom, labelVisibility, handleHover, checkInClock]);

  // Open the pin menu from the right-click itself. Deck.gl rewrites that
  // gesture as a normal click and used to clear the menu in the same turn,
  // so the open is deferred until after that click handler.
  useEffect(() => {
    if (!map || !onMarkerClick) return undefined;
    const mapDiv = map.getDiv();
    let gesture = 0;

    const openAt = (localX, localY, point) => {
      const overlay = overlayRef.current;
      if (!overlay || !point) return;
      const hit = overlay.pickObject({ x: localX, y: localY, radius: 20 });
      const marker = menuMarker(hit && hit.object);
      if (!marker) {
        if (!menuOpenedThisGesture) onEmptyRightClick?.();
        return;
      }
      noteMenuOpened();
      onMarkerClick(marker, { rightClick: true, shiftKey: false, point });
    };

    const onContextMenu = (e) => {
      if (!(e.target instanceof Node) || !mapDiv.contains(e.target)) return;
      markRightClickGesture();
      const serial = ++gesture;
      const rect = mapDiv.getBoundingClientRect();
      const point = { x: e.clientX, y: e.clientY };
      window.setTimeout(() => {
        if (serial !== gesture) return;
        openAt(e.clientX - rect.left, e.clientY - rect.top, point);
      }, 0);
    };

    // Google's pixel is the same coordinate space deck.gl uses for left-clicks.
    const onRightClick = map.addListener('rightclick', (e) => {
      const pixel = e && e.pixel;
      if (!pixel) return;
      markRightClickGesture();
      const serial = ++gesture;
      const dom = e && e.domEvent;
      const rect = mapDiv.getBoundingClientRect();
      const point = dom && typeof dom.clientX === 'number'
        ? { x: dom.clientX, y: dom.clientY }
        : (pixel ? { x: rect.left + pixel.x, y: rect.top + pixel.y } : null);
      window.setTimeout(() => {
        if (serial !== gesture || !pixel) return;
        openAt(pixel.x, pixel.y, point);
      }, 0);
    });

    window.addEventListener('contextmenu', onContextMenu, true);
    return () => {
      window.removeEventListener('contextmenu', onContextMenu, true);
      window.google.maps.event.removeListener(onRightClick);
    };
  }, [map, onMarkerClick, onEmptyRightClick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, []);

  const tipParent = map?.getDiv?.() || null;
  if (!checkInHover || !tipParent) return null;
  const width = tipParent.clientWidth || 0;
  const height = tipParent.clientHeight || 0;
  let left = checkInHover.x + 14;
  let top = checkInHover.y + 14;
  if (width && left + 260 > width) left = Math.max(8, checkInHover.x - 268);
  if (height && top + 28 * checkInHover.lines.length > height) {
    top = Math.max(8, checkInHover.y - 12 - 20 * checkInHover.lines.length);
  }
  return createPortal(
    <div className="checkin-map-tip" style={{ left, top }}>
      {checkInHover.lines.map((line, i) => <div key={i}>{line}</div>)}
    </div>,
    tipParent
  );
}

/**
 * Auto-fit the map to current search hits, then restore the original
 * viewport when search clears. Debounced so typing doesn't yank the
 * camera on every keystroke. Single-result hops to z=13 instead of
 * fitBounds (which would zoom to street level).
 */
function SearchAutoFit({ searchActive, searchQuery, fitPoints }) {
  const map = useMap();
  const savedViewRef = useRef(null);
  const debounceRef = useRef(null);

  // Keep the latest fit points in a ref so they're available at fire-time
  // WITHOUT being an effect trigger. Only a change in the search *query*
  // should move the camera — a background data refresh (60s poll / manual
  // refresh) rebuilds this array with the same query and must not re-fit.
  const fitPointsRef = useRef(fitPoints);
  fitPointsRef.current = fitPoints;

  useEffect(() => {
    if (!map) return;

    if (!searchActive) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (savedViewRef.current) {
        const { center, zoom } = savedViewRef.current;
        if (center) map.panTo(center);
        if (typeof zoom === 'number') map.setZoom(zoom);
        savedViewRef.current = null;
      }
      return;
    }

    // Save the pre-search viewport once so we can restore it on clear.
    if (savedViewRef.current === null) {
      const c = map.getCenter();
      savedViewRef.current = {
        center: c ? c.toJSON() : null,
        zoom: map.getZoom(),
      };
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const fitPoints = fitPointsRef.current;
      if (!fitPoints || fitPoints.length === 0) return;
      if (fitPoints.length === 1) {
        map.panTo(fitPoints[0]);
        map.setZoom(13);
      } else if (window.google?.maps?.LatLngBounds) {
        const bounds = new window.google.maps.LatLngBounds();
        fitPoints.forEach(p => bounds.extend(p));
        map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // Keyed on the search query, NOT fitPoints — a data refresh that leaves
    // the query unchanged must not yank the camera back to a full-results fit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, searchActive, searchQuery]);

  return null;
}

/** Keep the map-type control off the floating left panels. */
function MapControlPlacement() {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps?.ControlPosition) return;
    const pos = window.google.maps.ControlPosition;
    map.setOptions({
      mapTypeControlOptions: { position: pos.TOP_RIGHT },
      fullscreenControlOptions: { position: pos.RIGHT_CENTER },
    });
  }, [map]);
  return null;
}

/**
 * CoordinatorDeckGLMap — Multi-layer map for the coordinator view
 */
export default function CoordinatorDeckGLMap({
  tickets = [],
  fieldReps = [],
  liveReps = [],
  selectedIds = null,
  selectedMarker = null,
  onMarkerClick = () => {},
  onMessageClick = null,
  chatAnchor = null,
  onChatPoint = null,
  onLassoSelect = null,
  onDeselect = () => {},
  lockClickDeselect = false,
  iconConfig = null,
  clusterMaxZoom = DEFAULT_CLUSTER_MAX_ZOOM,
  ticketLabelField = 'siteName',
  labelBackground = true,
  labelVisibility = { tickets: true, fieldReps: true, liveReps: true },
  searchActive = false,
  searchQuery = '',
  mobileDetailOpen = false,
}) {
  const mapRef = useRef(null);
  const suppressDeselectRef = useRef(false);
  const messageHoverRef = useRef(false);
  const lassoClickSuppressRef = useRef(false);
  const longPressRef = useRef(false);
  const shiftHeldRef = useRef(false);
  const gestureShiftRef = useRef(false);

  useEffect(() => {
    const isShift = (e) => e.key === 'Shift' || e.code === 'ShiftLeft' || e.code === 'ShiftRight' || e.shiftKey;
    const onDown = (e) => {
      if (isShift(e)) shiftHeldRef.current = true;
    };
    const onUp = (e) => {
      if (e.key === 'Shift' || e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        shiftHeldRef.current = false;
      }
    };
    const onBlur = () => { shiftHeldRef.current = false; };
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    document.addEventListener('keydown', onDown, true);
    document.addEventListener('keyup', onUp, true);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', onUp, true);
      document.removeEventListener('keydown', onDown, true);
      document.removeEventListener('keyup', onUp, true);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Long-press (mobile) is treated as additive select, same as Shift-click.
  useEffect(() => {
    let timer = null;
    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const onStart = (e) => {
      if (e.pointerType && e.pointerType !== 'touch') return;
      if (e.touches && e.touches.length !== 1) return;
      clear();
      timer = window.setTimeout(() => {
        longPressRef.current = true;
      }, 500);
    };
    const onMove = () => {
      longPressRef.current = false;
      clear();
    };
    const onEnd = () => clear();
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      clear();
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const handleMessageClick = useCallback((marker) => {
    suppressDeselectRef.current = true;
    onMessageClick?.(marker);
    window.setTimeout(() => {
      suppressDeselectRef.current = false;
    }, 50);
  }, [onMessageClick]);

  const handleMarkerClick = useCallback((marker, meta = {}) => {
    if (lassoClickSuppressRef.current) return;
    suppressDeselectRef.current = true;
    if (meta.rightClick || rightClickActive()) {
      noteMenuOpened();
      onMarkerClick(marker, { rightClick: true, shiftKey: false, point: meta.point || null });
      window.setTimeout(() => {
        suppressDeselectRef.current = false;
      }, 400);
      return;
    }
    const additive = !!(meta.shiftKey || longPressRef.current || shiftHeldRef.current || gestureShiftRef.current);
    longPressRef.current = false;
    onMarkerClick(marker, { shiftKey: additive, rightClick: false, point: meta.point || null });
    window.setTimeout(() => {
      suppressDeselectRef.current = false;
      gestureShiftRef.current = false;
    }, 50);
  }, [onMarkerClick]);

  const handleLassoSelect = useCallback((selectedTickets) => {
    lassoClickSuppressRef.current = true;
    suppressDeselectRef.current = true;
    onLassoSelect?.(selectedTickets);
    window.setTimeout(() => {
      lassoClickSuppressRef.current = false;
      suppressDeselectRef.current = false;
    }, 80);
  }, [onLassoSelect]);

  // Compute map center from data
  const defaultCenter = { lat: 39.8283, lng: -98.5795 }; // US center
  const center = React.useMemo(() => {
    const allPoints = [
      ...tickets.map(t => ({ lat: t.siteLatitude, lng: t.siteLongitude })),
      ...fieldReps.map(r => ({ lat: r.repLat, lng: r.repLong }))
    ].filter(p => isFinite(p.lat) && isFinite(p.lng));

    if (allPoints.length === 0) return defaultCenter;

    const avgLat = allPoints.reduce((sum, p) => sum + p.lat, 0) / allPoints.length;
    const avgLng = allPoints.reduce((sum, p) => sum + p.lng, 0) / allPoints.length;
    return { lat: avgLat, lng: avgLng };
  }, [tickets, fieldReps]);

  // Lat/lng points to fit when a search is active. Reflects whatever the
  // visible layers contain (parent has already narrowed by search +
  // auto-toggled the relevant layers on).
  const fitPoints = React.useMemo(() => {
    if (!searchActive) return null;
    const pts = [];
    for (const t of tickets) {
      if (isFinite(t.siteLatitude) && isFinite(t.siteLongitude)) {
        pts.push({ lat: t.siteLatitude, lng: t.siteLongitude });
      }
    }
    for (const r of fieldReps) {
      if (isFinite(r.repLat) && isFinite(r.repLong)) {
        pts.push({ lat: r.repLat, lng: r.repLong });
      }
    }
    return pts;
  }, [searchActive, tickets, fieldReps]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || window._env?.GOOGLE_MAPS_API_KEY || '';

  return (
    <div className="map-container" style={{ width: '100%', height: '100%' }}>
      <APIProvider apiKey={apiKey}>
        <GoogleMap
          mapId={MAP_ID}
          defaultCenter={center}
          defaultZoom={5}
          gestureHandling="greedy"
          disableDefaultUI={false}
          streetViewControl={false}
          mapTypeControl={!mobileDetailOpen}
          fullscreenControl={!mobileDetailOpen}
          zoomControl={true}
          clickableIcons={false}
          style={{ width: '100%', height: '100%' }}
        >
          <CoordinatorOverlay
            tickets={tickets}
            fieldReps={fieldReps}
            liveReps={liveReps}
            selectedIds={selectedIds}
            onMarkerClick={handleMarkerClick}
            onEmptyRightClick={onDeselect}
            onMessageClick={onMessageClick ? handleMessageClick : null}
            messageHoverRef={messageHoverRef}
            iconConfig={iconConfig}
            clusterMaxZoom={clusterMaxZoom}
            ticketLabelField={ticketLabelField}
            labelBackground={labelBackground}
            labelVisibility={labelVisibility}
          />
          <SearchAutoFit searchActive={searchActive} searchQuery={searchQuery} fitPoints={fitPoints} />
          <PointerGestureTracker
            shiftHeldRef={shiftHeldRef}
            gestureShiftRef={gestureShiftRef}
            suppressDeselectRef={suppressDeselectRef}
          />
          {onLassoSelect && (
            <LassoSelectHandler
              tickets={tickets}
              onLassoSelect={handleLassoSelect}
              suppressRef={suppressDeselectRef}
              shiftHeldRef={shiftHeldRef}
            />
          )}
          <DeselectHandler
            onDeselect={onDeselect}
            suppressRef={suppressDeselectRef}
            messageHoverRef={messageHoverRef}
            lockClickDeselect={lockClickDeselect}
            shiftHeldRef={shiftHeldRef}
            gestureShiftRef={gestureShiftRef}
          />
          <MapControlPlacement />
          <FocusSelectedForSheet
            selectedMarker={selectedMarker}
            enabled={mobileDetailOpen}
            sheetHeightRatio={0.7}
          />
          {onChatPoint && (
            <ChatAnchorTracker anchor={chatAnchor} onPoint={onChatPoint} />
          )}
        </GoogleMap>
      </APIProvider>
    </div>
  );
}
